/**
 * FERP Portal - Search Result Card Component
 *
 * Renders a single cross-subgraph search result with full context:
 * - Entity icon and display value
 * - Subgraph badge (color from metadata)
 * - Entity type label
 * - Life-critical indicator
 * - Identifier fields (parsed from JSON)
 * - Hover preview card (500ms delay)
 *
 * FERP Principles:
 * - Data-driven: All metadata from backend SubgraphMetadata
 * - Dumb frontend: Only displays what backend provides
 * - Mantine v8.x only (NO MUI)
 */

import { Badge, Group, Stack, Text, ThemeIcon, HoverCard } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { getIcon } from '@/utils/iconMap';
import { EntityPreviewCard } from './EntityPreviewCard';
import type { SearchResultItem } from '@/hooks/useEntitySearch';
import type { SubgraphInfo } from '@/hooks/useTenantSubgraphConfig';

interface SearchResultCardProps {
  result: SearchResultItem;
  subgraphInfo?: SubgraphInfo;
  onClick?: () => void;
}

export function SearchResultCard({ result, subgraphInfo, onClick }: SearchResultCardProps) {
  const IconComponent = getIcon(result.icon ?? 'IconBox');

  // Parse identifier fields from JSON
  let identifiers: Record<string, string> = {};
  if (result.identifiers) {
    try {
      identifiers = JSON.parse(result.identifiers);
    } catch {
      // Invalid JSON - ignore
    }
  }

  const identifierText = Object.entries(identifiers)
    .map(([key, value]) => `${key}: ${value}`)
    .join(' • ');

  return (
    <HoverCard width={320} shadow="md" openDelay={500} closeDelay={100}>
      <HoverCard.Target>
        <Group
          gap="md"
          p="sm"
          onClick={onClick}
          style={{ cursor: onClick ? 'pointer' : 'default' }}
        >
          {/* Left: Entity icon */}
          <ThemeIcon variant="light" size="lg" color="blue">
            <IconComponent size={20} />
          </ThemeIcon>

          {/* Center: Display value + identifier subtitle */}
          <Stack gap={2} style={{ flex: 1 }}>
            <Group gap="xs">
              <Text fw={600} size="sm">
                {result.displayValue}
              </Text>
              {result.isLifeCritical && (
                <Badge
                  color="red"
                  size="xs"
                  leftSection={<IconAlertCircle size={12} />}
                >
                  LC
                </Badge>
              )}
            </Group>
            {identifierText && (
              <Text size="xs" c="dimmed">
                {identifierText}
              </Text>
            )}
          </Stack>

          {/* Right: Subgraph badge + entity type */}
          <Stack gap={4} ta="right">
            {subgraphInfo && (
              <Badge color={subgraphInfo.color} variant="filled" size="sm">
                {subgraphInfo.displayName}
              </Badge>
            )}
            <Text size="xs" c="dimmed">
              {result.entityType}
            </Text>
          </Stack>
        </Group>
      </HoverCard.Target>

      <HoverCard.Dropdown>
        <EntityPreviewCard entityType={result.entityType} entityId={result.id} />
      </HoverCard.Dropdown>
    </HoverCard>
  );
}

export default SearchResultCard;
